'use client';

import { FC, useState, useRef, useEffect, MouseEvent } from 'react';
import { clsx } from 'clsx';

import { clientSideMessages, useLocale, useTranslations } from '@monoimin/intl';

interface CopyLinkProps {
  id?: string;
}

export const AnchorCopyTooltip: FC<CopyLinkProps> = ({ id }) => {
  const spanRef = useRef<HTMLSpanElement>(null);
  const [copied, setCopied] = useState(false);
  const [open, setOpen] = useState(false);
  const locale = useLocale();
  const getMessage = useTranslations(clientSideMessages[locale]);

  useEffect(() => {
    const parentElement = spanRef.current?.parentElement;
    if (parentElement) {
      parentElement.addEventListener('click', onCopyClick);
    }
    return () => {
      if (parentElement) {
        parentElement.removeEventListener('click', onCopyClick);
      }
    };
  }, [spanRef.current]);

  if (!id) {
    return null;
  }

  const onCopyClick = async (event: MouseEvent<HTMLSpanElement> | Event) => {
    event.preventDefault();
    await copyLink();
  };

  const copyLink = async () => {
    const linkToCopy = `${window.location.origin}${window.location.pathname}#${id}`;
    if ('clipboard' in navigator) {
      await navigator.clipboard.writeText(linkToCopy);
      setCopied(true);
      setOpen(true);
      setTimeout(() => {
        setCopied(false);
      }, 2000);
      setTimeout(() => {
        setOpen(false);
      }, 3000);
    }
  };

  const text = copied
    ? getMessage('linkCopied')
    : getMessage('clickToCopyLink');
  return (
    <span
      ref={spanRef}
      onClick={onCopyClick}
      className={clsx(
        'heading-copy-link absolute pr-7 flex items-center group-hover/heading:-translate-x-7 group-hover/heading:opacity-100 hover:opacity-100 opacity-0 w-4 duration-150',
        {
          '-translate-x-7': open,
          'opacity-100': copied,
          'opacity-0': !open,
        }
      )}
      aria-label='Copy anchor link'
      data-copied={copied}
      data-tooltip-text={text}
    >
      &#8203;
      {/* пустой символ нужне для правильного отображения иконки по
      высоте первой строки без этого берет высоту всего элемента либо 0 */}
    </span>
  );
};
